import { useCallback, useEffect, useState } from 'react';

import type { Order } from '@notify/shared';
import { OrderStatus } from '@notify/shared';

import { shopService } from '../../services/shop.service';

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastItem { id: number; type: 'success' | 'error'; message: string; }

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const show = useCallback((type: ToastItem['type'], message: string) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, type, message }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: OrderStatus.PENDING, label: 'Pending' },
  { key: OrderStatus.PREPARING, label: 'Preparing' },
  { key: OrderStatus.READY, label: 'Ready' },
  { key: OrderStatus.COLLECTED, label: 'Collected' },
  { key: OrderStatus.CANCELLED, label: 'Cancelled' },
];

function formatTime(d: Date | null | string): string {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const styles: Record<OrderStatus, string> = {
    [OrderStatus.PENDING]: 'bg-gray-100 text-gray-600',
    [OrderStatus.PREPARING]: 'bg-amber-100 text-amber-700',
    [OrderStatus.READY]: 'bg-green-100 text-green-700',
    [OrderStatus.COLLECTED]: 'bg-blue-100 text-blue-700',
    [OrderStatus.CANCELLED]: 'bg-red-100 text-red-500',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}

const PAGE_SIZE = 20;

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const [updatingOrder, setUpdatingOrder] = useState<string | null>(null);
  const { toasts, show: showToast } = useToast();

  useEffect(() => {
    setPage(1);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFilter]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params: { status?: string; date?: string } = { date: dateFilter };
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await shopService.getOrders(params);
      setOrders(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(orderId: string, status: OrderStatus) {
    setUpdatingOrder(orderId);
    try {
      const updated = await shopService.updateOrderStatus(orderId, status);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? updated : o)));
      showToast('success', `Order marked ${status}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setUpdatingOrder(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const paged = orders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="space-y-5">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status tabs */}
          <div className="flex gap-1 rounded-lg bg-[#F1F5F9] p-1 flex-wrap">
            {STATUS_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === key
                    ? 'bg-white text-[#0F172A] shadow-sm'
                    : 'text-[#64748B] hover:text-[#0F172A]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Date picker */}
          <input
            type="date"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-[#F8FAFC]">
                <tr>
                  {['Reference', 'Status', 'Created', 'Deadline', 'Notified', 'Subs', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 animate-pulse rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#64748B]">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  paged.map((order) => (
                    <tr key={order.id} className="hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-3 font-mono font-semibold text-[#0F172A]">
                        {order.orderReference}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3 text-[#64748B]">{formatTime(order.createdAt)}</td>
                      <td className="px-4 py-3 text-[#64748B]">{formatTime(order.prepDeadline)}</td>
                      <td className="px-4 py-3 text-[#64748B]">{formatTime(order.notifiedAt)}</td>
                      <td className="px-4 py-3 text-[#64748B]">—</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {order.status === OrderStatus.PENDING && (
                            <button
                              type="button"
                              disabled={updatingOrder === order.id}
                              onClick={() => void handleStatusChange(order.id, OrderStatus.PREPARING)}
                              className="rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                            >
                              Prepare
                            </button>
                          )}
                          {order.status === OrderStatus.PREPARING && (
                            <button
                              type="button"
                              disabled={updatingOrder === order.id}
                              onClick={() => void handleStatusChange(order.id, OrderStatus.READY)}
                              className="rounded bg-green-500 px-2 py-1 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-50"
                            >
                              Ready
                            </button>
                          )}
                          {order.status === OrderStatus.READY && (
                            <button
                              type="button"
                              disabled={updatingOrder === order.id}
                              onClick={() => void handleStatusChange(order.id, OrderStatus.COLLECTED)}
                              className="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                            >
                              Collected
                            </button>
                          )}
                          {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.COLLECTED && (
                            <button
                              type="button"
                              disabled={updatingOrder === order.id}
                              onClick={() => void handleStatusChange(order.id, OrderStatus.CANCELLED)}
                              className="rounded border border-gray-200 px-2 py-1 text-xs text-[#64748B] hover:bg-gray-50 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <p className="text-xs text-[#64748B]">
                {orders.length} orders · page {page} of {totalPages}
              </p>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-gray-200 px-3 py-1 text-xs font-medium text-[#64748B] hover:bg-gray-50 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-gray-200 px-3 py-1 text-xs font-medium text-[#64748B] hover:bg-gray-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
          {toasts.map((t) => (
            <div key={t.id} className={`rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${t.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
